import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ShieldOff, SplitSquareVertical } from "lucide-react";
import { useState } from "react";

import {
  AgreementRateByBucketChart,
  AgreementRateSplitChart,
  CreditCultureChart,
} from "../components/AgreementCultureCharts";
import { FilterBar } from "../components/FilterBar";
import { PageHeader } from "../components/PageHeader";
import { StepShell, getStep } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { useAgreementCultureSummary } from "../lib/api/agreement-culture";
import type { SegmentFilter } from "../lib/api/types";
import { useDashboardSearch } from "../lib/useDashboardSearch";

const SUBTITLE =
  "How often underwriters agree with Athena — and where a whole slice of the book is quietly drifting.";

const SEGMENTS: { value: SegmentFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "PG", label: "PG" },
  { value: "Corp", label: "Corp" },
];

function EmptyState() {
  return (
    <Card className="border-neutral-inactive/40">
      <CardContent className="flex flex-col items-center gap-3 px-6 py-16 text-center">
        <ShieldOff className="size-8 text-neutral-inactive" />
        <p className="text-base font-semibold text-neutral-inactive">
          No data — not yet live on Athena
        </p>
        <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
          Agreement rate is only measurable where Athena is making recommendations. Select the live
          office or &ldquo;All Offices&rdquo; to see this data.
        </p>
      </CardContent>
    </Card>
  );
}

export function AgreementCulture() {
  const { range, office, compare, segment, update } = useDashboardSearch();
  const location = useLocation();
  const [view, setView] = useState<"split" | "buckets">("split");

  const { data, isPending, isError, error } = useAgreementCultureSummary(segment, {
    ...(range ? { range } : {}),
    ...(office ? { office } : {}),
    ...(compare != null ? { compare } : {}),
  });

  const setSegment = (value: string) => {
    if (!value) return;
    update({ segment: value as SegmentFilter });
  };

  return (
    <StepShell step={getStep("agreement-culture")}>
      <PageHeader title="Agreement Rate and Credit Culture" subtitle={SUBTITLE} />
      <FilterBar />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <SplitSquareVertical className="size-4 text-primary" />
          Segment
        </span>
        <ToggleGroup
          type="single"
          value={segment}
          onValueChange={setSegment}
          variant="outline"
          size="sm"
          aria-label="PG / Corp segment"
        >
          {SEGMENTS.map((item) => (
            <ToggleGroupItem key={item.value} value={item.value} className="px-4">
              {item.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <span className="text-xs text-muted-foreground">
          Applies to Agreement Rate Trend only — Credit Culture always shows the aggregate.
        </span>
      </div>

      {isError ? (
        <Card>
          <CardContent className="p-6 text-sm text-decline" role="alert">
            Could not load agreement rate data: {(error as Error).message}
          </CardContent>
        </Card>
      ) : isPending || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-[360px] rounded-lg" />
          <Skeleton className="h-[400px] rounded-lg" />
        </div>
      ) : !data.dataAvailable ? (
        <EmptyState />
      ) : (
        <div className="space-y-8">
          {/* Section 1 — the headline trend.
              Titled from data.metricLabel rather than hardcoded, because the backend may be
              serving approval rate instead of agreement rate — agreement with Athena is not
              measurable on this data. Hardcoding "Agreement Rate" here would mislabel real
              numbers, which is worse than showing nothing. */}
          <Card>
            <CardHeader>
              <CardTitle>{data.metricLabel} Trend</CardTitle>
              <CardDescription>
                {data.metric === "agreement_rate"
                  ? "How often the final decision matched Athena's recommendation."
                  : "How often a decided application was approved."}{" "}
                Small-ticket means deals under $
                {data.smallTicketThresholdDollars.toLocaleString("en-US")}.
              </CardDescription>
              {/* Visible caption, not a tooltip: a reader who does not know the metric was
                  substituted will draw the wrong conclusion from a correct chart. */}
              {data.metricNote ? (
                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  {data.metricNote}
                </p>
              ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
              <ToggleGroup
                type="single"
                value={view}
                onValueChange={(value) => value && setView(value as "split" | "buckets")}
                variant="outline"
                size="sm"
                aria-label="Agreement rate view"
              >
                <ToggleGroupItem value="split" className="px-4">
                  Small-ticket vs. Rest
                </ToggleGroupItem>
                <ToggleGroupItem value="buckets" className="px-4">
                  Full ticket-size breakdown
                </ToggleGroupItem>
              </ToggleGroup>

              {view === "split" ? (
                <AgreementRateSplitChart data={data.agreementRateTrend} />
              ) : (
                <AgreementRateByBucketChart data={data.agreementRateByBucket} />
              )}

              <p className="text-xs leading-relaxed text-muted-foreground">
                Small-ticket agreement rate is the specific signal behind Win 2 — once this holds
                high and stable, the auto-approval case is ready. The $
                {data.smallTicketThresholdDollars.toLocaleString("en-US")} cutoff is being tested
                against real evidence on the{" "}
                <Link
                  to={`/underwriting-exceptions/productivity${location.search}`}
                  className="inline-flex items-center gap-1 font-medium text-primary underline decoration-dotted underline-offset-4 hover:no-underline"
                >
                  Automation Readiness Matrix
                  <ArrowRight className="size-3" />
                </Link>
              </p>
            </CardContent>
          </Card>

          {/* Section 2 — Credit Culture Signal */}
          <Card>
            <CardHeader>
              <CardTitle>Credit Culture Signal</CardTitle>
              <CardDescription>
                Agreement rate by industry, aggregated across PG and Corp. A single industry pulling
                away from the pack is the thing to notice.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CreditCultureChart series={data.creditCultureTrend} />
              <p className="text-xs leading-relaxed text-muted-foreground">
                This is one half of the drift story —{" "}
                <Link
                  to={`/underwriting-exceptions/policy-compliance${location.search}`}
                  className="inline-flex items-center gap-1 font-medium text-primary underline decoration-dotted underline-offset-4 hover:no-underline"
                >
                  Policy Compliance Rate by Tier shows the credit-grade side of the same question
                  <ArrowRight className="size-3" />
                </Link>
              </p>
            </CardContent>
          </Card>

          <p className="text-xs leading-relaxed text-muted-foreground/70">
            An office-cut view of agreement rate — same chart, office as the series instead of
            industry — becomes useful once more offices are live. Not built yet; today it would just
            be four empty lines next to one real one.
          </p>
        </div>
      )}
    </StepShell>
  );
}
